import React, { useState, useEffect, useCallback } from 'react';
import { Asset } from '../../types';
import { getAssets, createAsset } from '../../services/marketingApi';
import { Loading, Modal, Thumb, btn, btnTone, input, picked, trClick } from '../../components/Kit';

interface AssetPickerModalProps {
  isOpen: boolean;
  assetType: 'image' | 'video' | 'pdf';
  onSelect: (asset: Asset) => void;
  onClose: () => void;
}

const AssetPickerModal: React.FC<AssetPickerModalProps> = ({
  isOpen,
  assetType,
  onSelect,
  onClose,
}) => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [tagsFilter, setTagsFilter] = useState('');
  const [uploading, setUploading] = useState(false);
  const [currentFolder, setCurrentFolder] = useState<string | null>(null);
  const [subfolders, setSubfolders] = useState<string[]>([]);

  const getModalTitle = () => {
    switch (assetType) {
      case 'image':
        return 'Select Image';
      case 'video':
        return 'Select Video';
      case 'pdf':
        return 'Select Document';
      default:
        return 'Select Asset';
    }
  };

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAssets(tagsFilter || undefined, assetType, currentFolder);
      setAssets(response.assets);
      setSubfolders(response.subfolders || []);
    } catch (err) {
      console.error('Error fetching assets:', err);
      setError('Failed to load assets. Please try again.');
    }
    setLoading(false);
  }, [assetType, tagsFilter, currentFolder]);

  useEffect(() => {
    if (isOpen) {
      fetchAssets();
      setSelectedAsset(null);
      setSearchQuery('');
      setCurrentFolder(null);
    }
  }, [isOpen, fetchAssets]);

  const filteredAssets = assets.filter((asset) => {
    if (!searchQuery) return true;
    return asset.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleSelect = () => {
    if (selectedAsset) {
      onSelect(selectedAsset);
    }
  };

  const handleCardClick = (asset: Asset) => {
    setSelectedAsset(asset);
  };

  const handleCardDoubleClick = (asset: Asset) => {
    onSelect(asset);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('name', file.name.replace(/\.[^/.]+$/, '')); // Remove extension for name
      formData.append('type', assetType);
      formData.append('file', file);
      formData.append('tags', 'whatsapp');

      const newAsset = await createAsset(formData);
      setAssets((prev) => [newAsset, ...prev]);
      setSelectedAsset(newAsset);
    } catch (err) {
      console.error('Error uploading asset:', err);
      setError('Failed to upload asset. Please try again.');
    }
    setUploading(false);
    // Reset the input
    e.target.value = '';
  };

  const getAcceptTypes = () => {
    switch (assetType) {
      case 'image':
        return 'image/jpeg,image/png,image/webp';
      case 'video':
        return 'video/mp4,video/3gpp';
      case 'pdf':
        return 'application/pdf';
      default:
        return '*/*';
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      title={getModalTitle()}
      onClose={onClose}
      footer={<>
        <span className="flex-1 min-w-0 text-[13px] truncate">
          {selectedAsset
            ? <>Selected <span className="text-forest font-semibold">{selectedAsset.name}</span></>
            : <span className="text-graphite">Nothing selected</span>}
        </span>
        <button type="button" onClick={onClose} className={`${btn} ${btnTone.charcoal}`}>Cancel</button>
        <button type="button" onClick={handleSelect} disabled={!selectedAsset} className={`${btn} ${btnTone.forest}`}>Use this {assetType === 'pdf' ? 'document' : assetType}</button>
      </>}
    >
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <input
          type="search"
          placeholder="Search by name"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={`${input} flex-1 min-w-[160px]`}
        />
        <input
          type="text"
          placeholder="Tags · Enter to filter"
          value={tagsFilter}
          onChange={(e) => setTagsFilter(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              fetchAssets();
            }
          }}
          className={`${input} flex-1 min-w-[160px]`}
        />
        <label className={`${btn} ${btnTone.charcoal} !h-9 cursor-pointer ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
          <input type="file" accept={getAcceptTypes()} onChange={handleFileUpload} className="hidden" disabled={uploading} />
          {uploading ? 'Uploading…' : '↑ Upload'}
        </label>
        <button type="button" onClick={fetchAssets} aria-label="Refresh" className={`${btn} ${btnTone.ghost} !h-9`}>↻</button>
      </div>

      <div className="flex items-center gap-1.5 pb-2 text-[12.5px] border-b border-charcoal/15">
        <button
          type="button"
          onClick={() => setCurrentFolder(null)}
          className={`hover:text-forest ${!currentFolder ? 'text-charcoal font-semibold' : 'text-graphite'}`}
        >
          All assets
        </button>
        {currentFolder && currentFolder.split('/').map((segment, i, arr) => {
          const path = arr.slice(0, i + 1).join('/');
          const isLast = i === arr.length - 1;
          return (
            <React.Fragment key={path}>
              <span className="text-charcoal/30">/</span>
              <button
                type="button"
                onClick={() => setCurrentFolder(path)}
                className={`hover:text-forest ${isLast ? 'text-charcoal font-semibold' : 'text-graphite'}`}
              >
                {segment}
              </button>
            </React.Fragment>
          );
        })}
      </div>

      {loading ? (
        <Loading what="assets" />
      ) : error ? (
        <div className="py-6">
          <div className="rounded-lg bg-brick/5 text-brick px-4 py-3 text-[13.5px]">{error}</div>
          <button type="button" onClick={fetchAssets} className={`${btn} ${btnTone.ghost} mt-2`}>Try again</button>
        </div>
      ) : filteredAssets.length === 0 && subfolders.length === 0 ? (
        <div className="text-center text-graphite text-[13px] py-10">No {assetType === 'pdf' ? 'documents' : `${assetType}s`} here — upload one or change the filters.</div>
      ) : (
        <div>
          {subfolders.map((folderName) => (
            <div
              key={`folder-${folderName}`}
              onClick={() => setCurrentFolder(currentFolder ? `${currentFolder}/${folderName}` : folderName)}
              className={`${trClick(false)} flex items-center gap-3 px-2 py-2`}
            >
              <span className="w-16 h-11 flex-none grid place-items-center rounded-md bg-charcoal/[0.06] text-graphite">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </span>
              <span className="font-semibold text-[13.5px] flex-1 truncate">{folderName}</span>
              <span className="text-charcoal/25" aria-hidden>›</span>
            </div>
          ))}
          {filteredAssets.map((asset) => {
            const on = selectedAsset?.id === asset.id;
            return (
              <div
                key={asset.id}
                onClick={() => handleCardClick(asset)}
                onDoubleClick={() => handleCardDoubleClick(asset)}
                className={`${trClick(on)} ${picked(on)} flex items-center gap-3 px-2 py-2`}
              >
                {asset.type === 'pdf'
                  ? <span className="w-16 h-11 flex-none grid place-items-center rounded-md bg-charcoal/[0.06] text-graphite text-[11px] font-semibold">PDF</span>
                  : <Thumb src={asset.url} video={asset.type === 'video'} />}
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-[13.5px] truncate" title={asset.name}>{asset.name}</div>
                  {asset.tags.length > 0 && (
                    <div className="text-[11.5px] text-graphite truncate">{asset.tags.join(' · ')}</div>
                  )}
                </div>
                {on && <span className="text-forest text-[13px] font-semibold" aria-hidden>✓</span>}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};

export default AssetPickerModal;
